import React, {useEffect, useState} from 'react';

import {
	ComboBox,
	DeprecatedCreativeModal as CreativeModal,
	DatePicker,
	FlexColumn,
	FlexRow,
	IconTooltip,
	NumberField,
	SubHeading,
	Text,
	TextField,
} from '@forecast-it/design-system';
import styled from 'styled-components';
import {createFragmentContainer, graphql} from 'react-relay';
import type {BulkInvoiceCreateModal_viewer$data as BulkInvoiceCreateModal_viewer} from './__generated__/BulkInvoiceCreateModal_viewer.graphql';
import {createInvoices, getCurrencyItems, getInvoicePeriodData, InvoicePeriodDTO} from './BulkInvoiceCreateLogic';
import {MODAL_TYPE, showModal} from '../../../generic_modal_conductor';
import {getNotNullishNodes, notNullishAndPredicate} from '../../../../../util/NotNullPredicate';
import CustomScrollDiv from '../../../../../../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import {useIntl} from 'react-intl';
import {ForecastTooltipFormulaRenderer} from '../../../../../../../components/ForecastTooltipFormulaRenderer.js';
import {sortByStartDate} from '../../../../../../project-tab/projects/project-retainer-periods/RetainerPeriodUtil';
import {trackEvent} from './../../../../../../../tracking/amplitude/TrackingV2';
import {projectUrl} from '../../../../../../../directApi';
import {hasFeatureFlag} from '../../../../../util/FeatureUtil';
import {RoundingEntities} from '../../../../../util/RoundingUtil';

export type BulkInvoiceCreateModal_company = BulkInvoiceCreateModal_viewer['company'];
export type BulkInvoiceCreateModal_project = BulkInvoiceCreateModal_viewer['project'];
export type BulkInvoiceCreateModal_rateCard = NonNullable<BulkInvoiceCreateModal_project>['rateCard'];
export type BulkInvoiceCreateModal_retainerPeriod = NonNullable<
	NonNullable<NonNullable<NonNullable<BulkInvoiceCreateModal_project>['retainerPeriods']>['edges']>[0]
>['node'];
export type BulkInvoiceCreateModal_timeRegistrationEdges = NonNullable<
	NonNullable<BulkInvoiceCreateModal_project>['timeRegistrations']
>['edges'];

const GridRow = styled.div`
	display: grid;
	grid-template-columns: auto 7% 12% 16% 16% 16%;
	column-gap: ${props => props.theme.sys.spacing[200]};
	width: 100%;
	border-bottom: solid 1px ${props => props.theme.sys.color.border.decorative};
	padding: ${props => props.theme.sys.spacing[300]};
	&:first-child {
		padding-top: ${props => props.theme.sys.spacing[100]};
	}
`;

interface InvoicePeriodState extends InvoicePeriodDTO {
	setName: (name: string | null) => void;
	setQuantity: (quantity: number) => void;
	setUnitPrice: (unitPrice: number) => void;
	setInvoiceDate: (invoiceDate: string | null) => void;
	setDueDate: (dueDate: string | null) => void;
	setErrorMessage: (errorMessage?: string) => void;
}

export interface InvoiceDTO extends InvoicePeriodDTO {
	currency: string;
	tax: number;
}

function getAmount(invoice: InvoicePeriodDTO | undefined, tax: number) {
	return invoice ? invoice.quantity * invoice.unitPrice + invoice.quantity * invoice.unitPrice * tax : 0;
}

function getInvoices(invoices: InvoicePeriodDTO[], currency: string, tax: number): InvoiceDTO[] | undefined {
	return invoices?.map(invoice => {
		return {
			...invoice,
			currency: currency,
			tax: tax,
		};
	});
}

function getSelectedPeriods(project: BulkInvoiceCreateModal_project, projectPeriodIds: string[]) {
	return getNotNullishNodes(project?.retainerPeriods?.edges).filter(period => period && projectPeriodIds.includes(period.id));
}

function getCurrency(
	project: BulkInvoiceCreateModal_project | null | undefined,
	company: BulkInvoiceCreateModal_company | null | undefined
) {
	return project?.rateCard?.currency || company?.currency || '';
}
interface BulkInvoiceCreateModalProps {
	projectPeriodIds: string[];
	viewer: BulkInvoiceCreateModal_viewer;
	closeModal: () => void;
	onStay: () => void;
}

function BulkInvoiceCreateModal({viewer, projectPeriodIds, closeModal, onStay}: BulkInvoiceCreateModalProps): JSX.Element {
	const {project, company} = viewer;
	const {formatMessage} = useIntl();
	const hasRateRounding = hasFeatureFlag('rate_rounding');
	const projectOrCompanyCurrency = getCurrency(project, company);
	const periods = getSelectedPeriods(project, projectPeriodIds).sort(sortByStartDate);

	const [tax, setTax] = useState(0);
	const [currency, setCurrency] = useState(projectOrCompanyCurrency);

	const invoicePeriodData: InvoicePeriodDTO[] = getInvoicePeriodData({periods, currency, project, company});
	const invoices: InvoicePeriodState[] =
		invoicePeriodData?.filter(notNullishAndPredicate()).map(dto => {
			const [name, setName] = useState(dto.name);
			const [quantity, setQuantity] = useState(dto.quantity);
			const [unitPrice, setUnitPrice] = useState(dto.unitPrice);
			const [invoiceDate, setInvoiceDate] = useState(dto.invoiceDate);
			const [dueDate, setDueDate] = useState(dto.dueDate);
			const [errorMessage, setErrorMessage] = useState<string>();
			return {
				period: dto.period,
				name,
				setName,
				quantity,
				setQuantity,
				unitPrice,
				setUnitPrice,
				invoiceDate,
				setInvoiceDate,
				dueDate,
				setDueDate,
				errorMessage,
				setErrorMessage,
			};
		}) || [];

	useEffect(() => {
		invoices.forEach((invoice, index) => {
			invoice.setUnitPrice(invoicePeriodData[index].unitPrice);
		});
	}, [currency]);

	const onSuccess = () => {
		showModal(
			{
				type: MODAL_TYPE.ACTION_COMPLETE_GO_SOMEWHERE,
				title: 'Invoices created',
				content: 'Invoices have been created successfully, in Draft status.',
				linkButtonText: 'Go to Invoicing',
				link: `${projectUrl(project?.companyProjectId, project?.customProjectId)}/financials#invoicing`,
				closeButtonText: 'Stay here',
				onStay,
				dataCy: 'period-invoice-confirmation-modal',
			},
			true
		);
		trackEvent('Bulk Retainer Period Invoices', 'Created', {count: periods.length || 0});
		periods.forEach(period => {
			trackEvent('Retainer Period Invoice', 'Created', {locked: period.periodLocked});
		});
	};

	const handleSetInvoiceName = (invoice: InvoicePeriodState, value) => {
		invoice.setName(value);
		if (!value) {
			invoice.setErrorMessage(formatMessage({id: 'common.required_field'}));
		} else if (value.length > 191) {
			invoice.setErrorMessage(formatMessage({id: 'errors.name-too-long'}));
		} else {
			invoice.setErrorMessage(undefined);
		}
	};

	return (
		<CreativeModal
			size={'xxl'}
			closeModal={closeModal}
			creativeButtonText={formatMessage({id: 'invoicing.create_invoices'})}
			cancelButtonText={formatMessage({id: 'common.cancel'})}
			onCreateHandler={() =>
				createInvoices({
					invoices: getInvoices(invoices, currency, tax * 100),
					project: project,
					invoiceQuantityDecimals: company?.invoiceQuantityDecimals,
					onSuccess,
				})
			}
			onCancelHandler={closeModal}
			title={formatMessage({id: 'invoicing.create_invoices'})}
			disabled={invoices?.some(invoice => !!invoice.errorMessage)}
		>
			<FlexColumn>
				<GridRow>
					<FlexColumn>
						<SubHeading>{formatMessage({id: 'common.project'})}</SubHeading>
						<Text>{project?.name}</Text>
					</FlexColumn>
					<div></div>
					<div></div>
					<div></div>
					<NumberField
						label={formatMessage({id: 'common.tax'})}
						value={tax}
						format={NumberField.FORMAT.PERCENT}
						onChange={setTax}
					/>

					<ComboBox
						label={formatMessage({id: 'common.currency'})}
						items={getCurrencyItems(company)}
						value={currency}
						onChange={setCurrency}
						width={ComboBox.WIDTH.FLEX}
					>
						{currencyItem => (
							<ComboBox.Item textValue={currencyItem.currencyCode}>
								<Text>{currencyItem.currencyCode}</Text>
							</ComboBox.Item>
						)}
					</ComboBox>
				</GridRow>
				<br />
				<br />
				<GridRow>
					<SubHeading>{formatMessage({id: 'project_section.invoice_name'})}</SubHeading>
					<SubHeading>{formatMessage({id: 'common.quantity'})}</SubHeading>

					<SubHeading>{formatMessage({id: 'common.unit_price'})}</SubHeading>

					<FlexRow gap={'s'}>
						<SubHeading>{formatMessage({id: 'common.amount'})}</SubHeading>
						<IconTooltip width={'l'}>
							<ForecastTooltipFormulaRenderer
								matchDesignSystemFonts={true}
								items={[
									{
										title: formatMessage({id: 'common.amount'}),
										details: [
											formatMessage({id: 'common.quantity'}),
											'x',
											formatMessage({id: 'common.unit_price'}),
											'+',
											formatMessage({id: 'common.tax'}),
										],
									},
								]}
								translatedMessage={true}
							/>
						</IconTooltip>
					</FlexRow>

					<SubHeading>{formatMessage({id: 'invoicing.invoice_date'})}</SubHeading>
					<SubHeading>{formatMessage({id: 'common.due_date'})}</SubHeading>
				</GridRow>
				<CustomScrollDiv autoHeight autoHeightMax={'60vh'}>
					{invoices?.map((invoice, index) => {
						return (
							<GridRow>
								<TextField
									data-cy={`invoice-name-${index}`}
									onChange={e => handleSetInvoiceName(invoice, e.target.value)}
									errorText={invoice.errorMessage}
									value={invoice.name || ''}
								/>
								<NumberField
									data-cy={`quantity-${index}`}
									minValue={0}
									onChange={invoice.setQuantity}
									value={invoice.quantity}
								/>

								<NumberField
									data-cy={`unit-price-${index}`}
									minimumFractionDigits={2}
									maximumFractionDigits={hasRateRounding ? RoundingEntities.RATE.decimals : 2}
									onChange={invoice.setUnitPrice}
									value={invoice.unitPrice}
								/>
								<NumberField
									data-cy={`amount-${index}`}
									minimumFractionDigits={2}
									maximumFractionDigits={2}
									disabled={true}
									value={getAmount(invoice, tax)}
								/>
								<DatePicker
									data-cy={`invoice-date-${index}`}
									onChange={invoice.setInvoiceDate}
									value={invoice.invoiceDate ?? undefined}
								/>
								<DatePicker
									data-cy={`due-date-${index}`}
									onChange={invoice.setDueDate}
									value={invoice.dueDate ?? undefined}
								/>
							</GridRow>
						);
					})}
				</CustomScrollDiv>
			</FlexColumn>
		</CreativeModal>
	);
}

export const BulkInvoiceCreateModalQuery = graphql`
	query BulkInvoiceCreateModal_Query($projectId: ID) {
		viewer {
			component(name: "bulk_invoice_create_modal_query")
			actualPersonId
			...BulkInvoiceCreateModal_viewer @arguments(projectId: $projectId)
		}
	}
`;

export default createFragmentContainer(BulkInvoiceCreateModal, {
	viewer: graphql`
		fragment BulkInvoiceCreateModal_viewer on Viewer @argumentDefinitions(projectId: {type: "ID"}) {
			company {
				currency
				invoiceQuantityDecimals
				dateEffectiveExchangeRates {
					edges {
						node {
							id
							currency
							rate
							startDate
						}
					}
				}
			}
			project(internalId: $projectId) {
				id
				companyProjectId
				customProjectId
				name
				client {
					id
				}
				rateCard {
					id
					name
					currency
					parentRateCardId
					defaultRate
					rates(first: 10000) {
						edges {
							node {
								id
								rate
								startDate
								defaultRate
								role {
									id
									name
								}
							}
						}
					}
				}
				retainerPeriods(first: 100000) @connection(key: "Project_retainerPeriods", filters: []) {
					edges {
						node {
							id
							name
							startYear
							startMonth
							startDay
							endYear
							endMonth
							endDay
							periodPriceAmount
							periodHoursAmount
							periodBudgetType
							periodLocked
							periodLockedTime
							invoiced
						}
					}
				}
				timeRegistrations(first: 10000000) @connection(key: "Project_timeRegistrations", filters: []) {
					edges {
						node {
							id
							day
							month
							year
							createdAt
							updatedAt
							minutesRegistered
							billableMinutesRegistered
							invoiced
							price
							retainerConflictHandled
							rate
						}
					}
				}
			}
		}
	`,
});
